/**
 * Digital Pattern of Life (Digital PoL)
 * Aggregated view of all device activities: apps, browsing, calls, SMS, status
 * Includes sub-tabs for Data view and AI Insights
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Chip,
  IconButton,
  Collapse,
  Stack,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  Button,
  useTheme,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  GetApp as DownloadIcon,
  Map as MapIcon,
  AutoAwesome as AIIcon,
  Refresh as RefreshIcon,
  TableChart as TableIcon,
  Smartphone as SmartphoneIcon,
  Place as PlaceIcon,
  Public as PublicIcon,
  Phone as PhoneIcon,
  CellTower as CellTowerIcon,
  Info as StatusIcon,
} from '@mui/icons-material';
import EmptyState from '@signals/app-shell/components/common/EmptyState';
import pathfinderApi from '../../services/pathfinderApi';
import { parseBackendDateTime } from '@signals/app-shell/utils/parseDateTime';
import { useParticipantLabels } from '@signals/app-shell/hooks/useParticipantLabels';
import { useCourseLabels } from '@signals/app-shell/hooks/useCourseLabels';

interface DigitalPoLItem {
  id: string;
  timestamp: string;
  timeString: string;
  entity_id: string;
  entity_type: string | null;
  // 'phone' | 'watch' | 'vehicle'. Derived backend-side from the device
  // reference row that produced the event. Used to honor per-device toggles
  // in the Pathfinder Device List so AI insights and PowerPoint exports only
  // include the device classes the user has switched on.
  device_class?: string | null;
  activity_type: string;
  activity_data: Record<string, any>;
  latitude: number | null;
  longitude: number | null;
  metadata?: Record<string, any> | null;
}

interface MapBounds {
  west: number;
  south: number;
  east: number;
  north: number;
}

interface DigitalPoLProps {
  selectedCourse?: string;
  selectedStudents?: string[];
  timespan?: number; // days to look back when start/end are not set
  startDate?: Date | null;
  endDate?: Date | null;
  onDigitalPoLDataChange?: (data: DigitalPoLItem[], showOnMap: boolean) => void;
  onFocusLocation?: (location: [number, number] | null) => void; // click row to focus/restore map
  // When true, the data table is cropped to events whose lat/lon falls inside `mapBounds`.
  // The data emitted to the map (via onDigitalPoLDataChange) is intentionally NOT
  // filtered by this — the map keeps showing every loaded event so panning around
  // doesn't make markers vanish under the user's cursor.
  filterByMapExtent?: boolean;
  mapBounds?: MapBounds | null;
  // entityId -> array of toggled-on device keys like ["Student-34-phone", "Student-34-watch"].
  // Mirrors the shape used by every other Pathfinder tab. When provided, rows
  // whose `${entity_id}-${device_class}` is not present are excluded from the
  // table, the activity-type tile counts, the AI insights payload, and any
  // downstream export. Falsy / empty -> no device filter (legacy behavior).
  selectedDevices?: Record<string, string[]>;
}

type SortOrder = 'asc' | 'desc';

const DigitalPoL: React.FC<DigitalPoLProps> = ({
  selectedCourse,
  selectedStudents = [],
  timespan = 14,
  startDate,
  endDate,
  onDigitalPoLDataChange,
  onFocusLocation,
  filterByMapExtent = false,
  mapBounds = null,
  selectedDevices,
}) => {
  const theme = useTheme();
  const { plural, pluralLower, singularLower } = useParticipantLabels();
  const { courseSingular, coursePlural } = useCourseLabels();
  const isDarkMode = theme.palette.mode === 'dark';
  const rangeStart = startDate ?? null;
  const rangeEnd = endDate ?? null;
  
  const [activeTab, setActiveTab] = useState(0); // 0 = Data, 1 = AI Insights
  const [data, setData] = useState<DigitalPoLItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [activityTypeFilter, setActivityTypeFilter] = useState<string>('all');
  
  // Sorting
  const [sortBy, setSortBy] = useState<keyof DigitalPoLItem>('timestamp');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  
  // Expanded rows for details
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  
  // Map display
  const [showOnMap, setShowOnMap] = useState(false);

  // AI Insights state
  const [aiSummary, setAiSummary] = useState<string>('');
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  // Auto-fetch whenever the user has a valid course + entity selection so
  // navigating away from this tab and back doesn't force a manual reload.
  // The "Load Data" button below is kept as a manual refresh path.
  const fetchData = useCallback(async () => {
    if (!selectedCourse || selectedCourse === ('All ' + coursePlural)) {
      setError(`Please select a ${courseSingular.toLowerCase()}`);
      return;
    }

    if (!selectedStudents || selectedStudents.length === 0) {
      setError(`Please select at least one ${singularLower}`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params: any = {
        limit: 1000,
      };
      if (rangeStart && rangeEnd) {
        params.start_date = rangeStart.toISOString();
        params.end_date = rangeEnd.toISOString();
      } else {
        params.days = timespan;
      }

      if (selectedCourse && selectedCourse !== ('All ' + coursePlural)) {
        params.course_id = selectedCourse;
      }

      if (selectedStudents && selectedStudents.length > 0) {
        params.entity_ids = selectedStudents.join(',');
      }

      const fetchedData = await pathfinderApi.getDigitalPoL(params);
      // Normalize:
      //   1. Timestamps — parseBackendDateTime treats bare ISO strings as
      //      UTC, mirroring device track handling.
      //   2. `id` — the backend returns the raw primary key from whichever
      //      activity table the row came from (device_app_info.id,
      //      device_browsing_history.id, device_sms.id, etc.), and those
      //      sequences are INDEPENDENT per table. So an app_info row with
      //      id=42 and an SMS row with id=42 both arrive with `id: 42`.
      //      `<Fragment key={item.id}>` then sees a key collision and
      //      React's reconciler ends up reusing one row's DOM node for
      //      the other — the symptom is ghost rows from a previous filter
      //      stuck at the top of the table regardless of what filter you
      //      flip to, plus cross-contamination of selectedRowId and
      //      expandedRows (expanding sms id=42 would also expand
      //      app_info id=42). Namespacing the id with the activity_type
      //      gives us a globally unique key the rest of the component can
      //      keep treating as an opaque string.
      const normalized = (fetchedData || []).map((item: any) => {
        const parsed = parseBackendDateTime(item.timestamp);
        return {
          ...item,
          id: `${item.activity_type ?? 'unknown'}_${item.id}`,
          timestamp: parsed ? parsed.toISOString() : item.timestamp,
          timeString: parsed ? parsed.toLocaleString() : item.timestamp,
        };
      });
      setData(normalized);
    } catch (err: any) {
      console.error('Failed to fetch Digital PoL data:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [selectedCourse, selectedStudents, timespan, startDate, endDate, coursePlural, courseSingular, singularLower]);

  // Auto-load on mount and whenever the underlying selection changes. We
  // serialize selectedStudents because it's an array — using the array
  // reference in the dep list would fire on every parent re-render even when
  // the contents are unchanged. We also gate on having a valid selection so
  // we don't bombard the API with 400s while the user is still picking.
  const studentsKey = (selectedStudents || []).slice().sort().join(',');
  useEffect(() => {
    const hasCourse = !!selectedCourse && selectedCourse !== ('All ' + coursePlural);
    const hasStudents = !!selectedStudents && selectedStudents.length > 0;
    if (hasCourse && hasStudents) {
      fetchData();
    }
    // fetchData is intentionally excluded — it captures the same dependencies
    // we list explicitly here, so including it would double-trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCourse, studentsKey, timespan, startDate, endDate]);

  const handleSort = (column: keyof DigitalPoLItem) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const toggleRowExpansion = (id: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleRowClick = useCallback((item: DigitalPoLItem) => {
    if (!onFocusLocation) return;
    if (item.latitude == null || item.longitude == null) return;

    if (selectedRowId === item.id) {
      setSelectedRowId(null);
      onFocusLocation(null);
      return;
    }

    setSelectedRowId(item.id);
    onFocusLocation([item.latitude, item.longitude]);
  }, [onFocusLocation, selectedRowId]);

  // Honor the per-device toggles from the Pathfinder Device List. The
  // `selectedDevices` shape (entityId -> ["entityId-phone", "entityId-watch", ...])
  // is the same one extractDevicesEfficiently writes in PathfinderPage and is
  // the same shape StoryMode already filters against. We do the filter once
  // here so the table, the activity-type tiles, the AI insights payload, and
  // the downstream PowerPoint export all see the same device-respecting view
  // of `data`. If `selectedDevices` is undefined or has no entries we fall
  // back to the legacy "show everything" behavior so older callers and the
  // empty initial render aren't affected.
  const deviceFilteredData = useMemo(() => {
    if (!selectedDevices) return data;
    const allowedKeys = new Set<string>();
    let hasAnyEntries = false;
    (Object.values(selectedDevices) as string[][]).forEach((keys) => {
      if (!keys) return;
      hasAnyEntries = hasAnyEntries || keys.length > 0;
      keys.forEach((k) => allowedKeys.add(k));
    });
    // No device toggle state available yet (initial render before
    // extractDevicesEfficiently runs) -> don't filter, otherwise the tab
    // would look empty until the user interacts with the Device List.
    if (!hasAnyEntries) return data;
    return data.filter((item) => {
      // device_class is backend-computed (phone / watch / vehicle). Default
      // unknown / legacy rows to 'phone' since historically every event came
      // from the phone reference row.
      const cls = (item.device_class || 'phone').toLowerCase();
      const key = `${item.entity_id}-${cls}`;
      return allowedKeys.has(key);
    });
  }, [data, selectedDevices]);

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    let filtered = deviceFilteredData;

    // Apply activity type filter locally so the dropdown updates loaded data immediately.
    // 'apps' is a meta-token used by the stat tile: it lumps both install-event
    // (`app_info`) and foreground/usage event (`app_usage`) rows together so the
    // tile count and the resulting table size agree.
    if (activityTypeFilter && activityTypeFilter !== 'all') {
      if (activityTypeFilter === 'apps') {
        filtered = filtered.filter(
          (item) => item.activity_type === 'app_info' || item.activity_type === 'app_usage'
        );
      } else {
        filtered = filtered.filter((item) => item.activity_type === activityTypeFilter);
      }
    }
    
    // Apply search filter
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter((item) => {
        return (
          item.entity_id?.toLowerCase().includes(lowerSearch) ||
          item.activity_type?.toLowerCase().includes(lowerSearch) ||
          JSON.stringify(item.activity_data).toLowerCase().includes(lowerSearch)
        );
      });
    }
    
    // Sort
    const sorted = [...filtered].sort((a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];
      
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
      
      if (sortBy === 'timestamp') {
        const aTime = new Date(aVal as string).getTime();
        const bTime = new Date(bVal as string).getTime();
        return sortOrder === 'asc' ? aTime - bTime : bTime - aTime;
      }
      
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortOrder === 'asc' 
          ? aVal.localeCompare(bVal) 
          : bVal.localeCompare(aVal);
      }
      
      return sortOrder === 'asc' ? (aVal > bVal ? 1 : -1) : (bVal > aVal ? 1 : -1);
    });
    
    return sorted;
  }, [deviceFilteredData, activityTypeFilter, searchTerm, sortBy, sortOrder]);

  // Cropped to current map viewport, used by the table only. The map data
  // emitted via onDigitalPoLDataChange below stays based on `filteredAndSortedData`
  // so panning/zooming never makes points vanish from the map itself.
  const tableData = useMemo(() => {
    if (!filterByMapExtent || !mapBounds) return filteredAndSortedData;
    const { west, south, east, north } = mapBounds;
    const wraps = west > east; // viewport crosses the antimeridian
    return filteredAndSortedData.filter((item) => {
      const lat = item.latitude;
      const lon = item.longitude;
      if (lat == null || lon == null || !Number.isFinite(lat) || !Number.isFinite(lon)) return false;
      if (lat < south || lat > north) return false;
      return wraps ? (lon >= west || lon <= east) : (lon >= west && lon <= east);
    });
  }, [filteredAndSortedData, filterByMapExtent, mapBounds]);

  // Analyze Digital PoL data for AI insights.
  // Driven by `deviceFilteredData` (not raw `data`) so the AI summary and the
  // PowerPoint narrative built from this payload only describe the device
  // classes the user currently has toggled on in the Device List.
  const analyzeData = useMemo(() => {
    if (deviceFilteredData.length === 0) {
      return null;
    }

    // Group by activity type
    const byActivityType: Record<string, DigitalPoLItem[]> = {};
    const byEntity: Record<string, DigitalPoLItem[]> = {};
    const byHour: Record<number, number> = {};
    
    // Top apps, URLs, contacts
    const appUsage: Record<string, { count: number; totalTime: number }> = {};
    const browsingUrls: Record<string, number> = {};
    const callContacts: Record<string, { count: number; totalDuration: number }> = {};
    const smsContacts: Record<string, number> = {};

    deviceFilteredData.forEach((item) => {
      // Activity type breakdown
      if (!byActivityType[item.activity_type]) {
        byActivityType[item.activity_type] = [];
      }
      byActivityType[item.activity_type].push(item);

      // Entity breakdown
      if (!byEntity[item.entity_id]) {
        byEntity[item.entity_id] = [];
      }
      byEntity[item.entity_id].push(item);

      // Time patterns
      const date = new Date(item.timestamp);
      const hour = date.getHours();
      
      byHour[hour] = (byHour[hour] || 0) + 1;

      // Activity-specific aggregations
      switch (item.activity_type) {
        case 'app_usage':
          const packageName = item.activity_data.package_name || 'Unknown';
          const timeUsed = parseFloat(item.activity_data.total_time_used || '0');
          if (!appUsage[packageName]) {
            appUsage[packageName] = { count: 0, totalTime: 0 };
          }
          appUsage[packageName].count++;
          appUsage[packageName].totalTime += timeUsed;
          break;

        case 'browsing':
          const url = item.activity_data.url || 'Unknown';
          browsingUrls[url] = (browsingUrls[url] || 0) + 1;
          break;

        case 'call':
          const contact = item.activity_data.contact_name || item.activity_data.phone_number || 'Unknown';
          const duration = parseFloat(item.activity_data.duration || '0');
          if (!callContacts[contact]) {
            callContacts[contact] = { count: 0, totalDuration: 0 };
          }
          callContacts[contact].count++;
          callContacts[contact].totalDuration += duration;
          break;

        case 'sms':
          const smsContact = item.activity_data.phone_number || 'Unknown';
          smsContacts[smsContact] = (smsContacts[smsContact] || 0) + 1;
          break;
      }
    });

    // Get top items
    const topApps = Object.entries(appUsage)
      .sort((a, b) => b[1].totalTime - a[1].totalTime)
      .slice(0, 10)
      .map(([name, data]) => ({ name, ...data }));

    const topUrls = Object.entries(browsingUrls)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([url, count]) => ({ url, count }));

    const topCallContacts = Object.entries(callContacts)
      .sort((a, b) => b[1].count - a[1].count)
      .slice(0, 10)
      .map(([contact, data]) => ({ contact, ...data }));

    const topSmsContacts = Object.entries(smsContacts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([contact, count]) => ({ contact, count }));

    // Peak hours
    const peakHour = Object.entries(byHour)
      .sort((a, b) => b[1] - a[1])[0]?.[0];

    return {
      totalActivities: deviceFilteredData.length,
      activityBreakdown: Object.fromEntries(
        Object.entries(byActivityType).map(([type, items]) => [type, items.length])
      ),
      entityBreakdown: Object.fromEntries(
        Object.entries(byEntity).map(([entity, items]) => [entity, items.length])
      ),
      uniqueEntities: Object.keys(byEntity).length,
      dateRange: {
        start: deviceFilteredData[deviceFilteredData.length - 1]?.timestamp,
        end: deviceFilteredData[0]?.timestamp,
      },
      topApps,
      topUrls,
      topCallContacts,
      topSmsContacts,
      peakHour: peakHour ? parseInt(peakHour) : null,
    };
  }, [deviceFilteredData]);

  // Generate AI summary
  const generateAISummary = useCallback(async () => {
    // Gate on deviceFilteredData rather than raw data so we surface a clear
    // message when the user has all phone/watch/vehicle toggles off rather
    // than silently sending an empty payload to the AI.
    if (!analyzeData || deviceFilteredData.length === 0) {
      setAiError('No data available for analysis. Please load data first.');
      return;
    }

    setIsLoadingAI(true);
    setAiError(null);
    
    try {
      const summary = await pathfinderApi.generateDigitalPoLAI({
        entities: selectedStudents.length > 0 ? selectedStudents : Object.keys(analyzeData.entityBreakdown),
        totalActivities: analyzeData.totalActivities,
        activityBreakdown: analyzeData.activityBreakdown,
        entityBreakdown: analyzeData.entityBreakdown,
        uniqueEntities: analyzeData.uniqueEntities,
        dateRange: analyzeData.dateRange,
        topApps: analyzeData.topApps.slice(0, 5),
        topUrls: analyzeData.topUrls.slice(0, 5),
        topCallContacts: analyzeData.topCallContacts.slice(0, 5),
        topSmsContacts: analyzeData.topSmsContacts.slice(0, 5),
        peakHour: analyzeData.peakHour,
        course: selectedCourse || ('All ' + coursePlural),
        timespan,
      });

      setAiSummary(summary.summary);
    } catch (err: any) {
      console.error('❌ Failed to generate AI summary:', err);
      setAiError(err.response?.data?.detail || err.message || 'Failed to generate AI insights');
      setAiSummary('');
    } finally {
      setIsLoadingAI(false);
    }
  }, [analyzeData, deviceFilteredData, selectedStudents, selectedCourse, timespan, coursePlural]);

  // Notify parent when data or map visibility changes
  useEffect(() => {
    if (onDigitalPoLDataChange) {
      const dataWithLocation = filteredAndSortedData.filter(item => item.latitude && item.longitude);
      onDigitalPoLDataChange(dataWithLocation, showOnMap);
    }
  }, [filteredAndSortedData, showOnMap, onDigitalPoLDataChange]);

  useEffect(() => {
    if (!selectedRowId) return;
    if (tableData.some((item) => item.id === selectedRowId)) return;
    setSelectedRowId(null);
    onFocusLocation?.(null);
  }, [tableData, onFocusLocation, selectedRowId]);

  const getActivityTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      app_info: '#3b82f6',
      app_usage: '#8b5cf6',
      browsing: '#f59e0b',
      call: '#10b981',
      sms: '#06b6d4',
      status: '#ef4444',
    };
    return colors[type] || '#9e9e9e';
  };

  const mapSymbolLegend = [
    { type: 'app_info', label: 'App Info', detail: 'Install and app inventory events', icon: SmartphoneIcon },
    { type: 'app_usage', label: 'App Usage', detail: 'Foreground and usage duration events', icon: PlaceIcon },
    { type: 'browsing', label: 'Browsing', detail: 'Web activity events', icon: PublicIcon },
    { type: 'call', label: 'Calls', detail: 'Voice call records', icon: PhoneIcon },
    { type: 'sms', label: 'SMS / Cell Tower', detail: 'SMS markers use the map cell-tower symbol', icon: CellTowerIcon },
    { type: 'status', label: 'Device Status', detail: 'Device or radio state changes', icon: StatusIcon },
  ];

  // Render direction-aware text for SMS/call rows. The DB stores one row per
  // recipient, so the same message body legitimately appears multiple times
  // when sent to multiple numbers — without an explicit `To`/`From` prefix
  // analysts read these as duplicates. Outgoing => '→ To <num>', incoming
  // => '← From <num>'. Unknown direction falls back to a neutral bullet so
  // we never silently swallow malformed rows.
  const formatActivityData = (type: string, data: Record<string, any>) => {
    switch (type) {
      case 'app_info':
        if (data.installed_name || data.installed_package) {
          return `Installed: ${data.installed_name || data.installed_package}`;
        } else if (data.uninstalled_package) {
          return `Uninstalled: ${data.uninstalled_package}`;
        }
        return 'App Info';
      case 'app_usage':
        return `${data.package_name || 'Unknown'} - ${data.total_time_used || 'N/A'} (${data.app_status || 'N/A'})`;
      case 'browsing':
        return data.url || 'Unknown URL';
      case 'call': {
        const dir = (data.direction || data.call_type || '').toString().toLowerCase();
        const prefix =
          dir.includes('out') ? '→ To' :
          dir.includes('in') || dir.includes('miss') ? '← From' : '·';
        const num = data.contact_name || data.phone_number || 'Unknown';
        const dur = data.duration ? ` · ${data.duration}` : '';
        return `${prefix} ${num}${dur}`;
      }
      case 'sms': {
        const dir = (data.direction || '').toString().toLowerCase();
        const prefix =
          dir.includes('out') ? '→ To' :
          dir.includes('in') ? '← From' : '·';
        const num = data.phone_number || 'Unknown';
        const msg = (data.message || '').trim();
        const truncated = msg.length > 70 ? `${msg.substring(0, 70)}…` : msg;
        return truncated ? `${prefix} ${num} · ${truncated}` : `${prefix} ${num}`;
      }
      case 'status':
        return `${data.status_type || 'Status'}: ${data.status_state || 'N/A'}`;
      default:
        return JSON.stringify(data).substring(0, 100);
    }
  };

  const formatTimestamp = (item: DigitalPoLItem) => {
    return item.timeString || new Date(item.timestamp).toLocaleString();
  };

  const handleExportToPDF = () => {
    const headers = ['Timestamp', 'Entity', 'Type', 'Activity Details', 'Location'];
    const rows = tableData.map(item => [
      formatTimestamp(item),
      `${item.entity_id} (${item.entity_type || 'N/A'})`,
      item.activity_type,
      formatActivityData(item.activity_type, item.activity_data),
      item.latitude && item.longitude ? `${item.latitude.toFixed(6)}, ${item.longitude.toFixed(6)}` : 'N/A'
    ]);

    let extentNote = 'Map Extent Filter: OFF';
    if (filterByMapExtent && mapBounds) {
      const b: MapBounds = mapBounds;
      extentNote = `Map Extent Filter: ON (W ${b.west.toFixed(4)}, S ${b.south.toFixed(4)}, E ${b.east.toFixed(4)}, N ${b.north.toFixed(4)})`;
    }

    const content = [
      '='.repeat(100),
      'Digital Pattern of Life Report',
      `Generated: ${new Date().toLocaleString()}`,
      `${courseSingular}: ${selectedCourse || 'All'}`,
      `${plural}: ${selectedStudents && selectedStudents.length > 0 ? selectedStudents.join(', ') : 'All'}`,
      `Activity Type: ${activityTypeFilter}`,
      extentNote,
      `Total Records: ${tableData.length}`,
      '='.repeat(100),
      '',
      headers.join(' | '),
      '-'.repeat(100),
      ...rows.map(row => row.join(' | ')),
      '',
      '='.repeat(100),
    ].join('\n');

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `digital-pol-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportInsights = () => {
    const content = [
      '='.repeat(100),
      'Digital Pattern of Life - AI Insights Report',
      `Generated: ${new Date().toLocaleString()}`,
      `${courseSingular}: ${selectedCourse || 'All'}`,
      `Entities: ${selectedStudents.length > 0 ? selectedStudents.join(', ') : 'All'}`,
      `Timespan: ${rangeStart && rangeEnd ? `${rangeStart.toLocaleDateString()} – ${rangeEnd.toLocaleDateString()}` : `${timespan} days`}`,
      `Total Activities: ${analyzeData?.totalActivities || 0}`,
      '='.repeat(100),
      '',
      aiSummary || 'No insights available',
      '',
      '='.repeat(100),
    ].join('\n');

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `digital-pol-insights-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleToggleMap = () => {
    setShowOnMap(!showOnMap);
  };

  // Per-activity-type counts for the compact stat tiles. We bucket each
  // activity row into one of the five top-level categories the Digital PoL
  // table surfaces so the analyst sees the same shape they're about to read.
  // NOTE: must live above the early-return below — React requires every hook
  // to be called in the same order on every render.
  // Built from `deviceFilteredData` (not raw `data`) so the stat tiles always
  // agree with what's actually rendered in the table after the Device List
  // toggles are applied.
  const activityCounts = useMemo(() => {
    const counts = { app: 0, browsing: 0, call: 0, sms: 0, status: 0 } as Record<string, number>;
    for (const item of deviceFilteredData) {
      const t = (item.activity_type || '').toLowerCase();
      if (t === 'app_info' || t === 'app_usage') counts.app++;
      else if (t === 'browsing') counts.browsing++;
      else if (t === 'call') counts.call++;
      else if (t === 'sms') counts.sms++;
      else if (t === 'status') counts.status++;
    }
    return counts;
  }, [deviceFilteredData]);

  const filtersActive =
    activityTypeFilter !== 'all' || !!searchTerm.trim();

  // Show empty state if no data loaded
  if (data.length === 0 && !loading) {
    const hasSelection = !!selectedCourse
      && selectedCourse !== ('All ' + coursePlural)
      && !!selectedStudents
      && selectedStudents.length > 0;

    return (
      <Box sx={{ p: 1.5 }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          Digital Pattern of Life
        </Typography>

        <Typography
          color="text.secondary"
          sx={{ textAlign: 'center', p: 3 }}
        >
          {hasSelection
            ? 'Click "Load Data" to view Digital PoL activity.'
            : `Select a ${courseSingular.toLowerCase()} and ${pluralLower} to view Digital PoL activity.`}
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            onClick={fetchData}
            disabled={loading || !hasSelection}
          >
            {loading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            Load Data
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2, py: 0.5 }}>
            <Typography variant="caption">{error}</Typography>
          </Alert>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1.5 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ flex: 1 }}>
          Digital Pattern of Life
        </Typography>
        {data.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            {data.length.toLocaleString()} activities
          </Typography>
        )}
      </Stack>

      {/* Sub-tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1.5 }}>
        <Tabs
          value={activeTab}
          onChange={(_e, newValue) => setActiveTab(newValue)}
          sx={{
            minHeight: 36,
            '& .MuiTab-root': {
              minHeight: 36,
              fontSize: '0.75rem',
              textTransform: 'none',
              fontWeight: 500,
            },
          }}
        >
          <Tab icon={<TableIcon />} iconPosition="start" label="Data" />
          <Tab icon={<AIIcon />} iconPosition="start" label="AI Insights" />
        </Tabs>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5, py: 0.5 }}>
          <Typography variant="caption">{error}</Typography>
        </Alert>
      )}

      {/* Data Tab Content */}
      {activeTab === 0 && (
        <>
          {/* Load Data Button */}
          {data.length === 0 && (
            <Box sx={{ mb: 2 }}>
              <Button
                variant="contained"
                onClick={fetchData}
                disabled={loading || !selectedCourse || selectedCourse === ('All ' + coursePlural) || !selectedStudents || selectedStudents.length === 0}
              >
                {loading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
                Load Data
              </Button>
            </Box>
          )}

          {loading && data.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Compact 5-tile stat row (APPS / BROWSE / CALLS / SMS / STATUS).
                  Clicking a tile cross-filters the table to that activity type
                  (clicking the active one clears the filter), mirroring the
                  Dwell Analysis stat row's at-a-glance + drill-in pattern.
                  Each tile shows the total event count; when a filter is active
                  it dims the OTHER tiles and the active tile shows a checkmark
                  so the analyst can see at a glance that clicking actually did
                  something. `filter: 'apps'` is a meta-token consumed in
                  `filteredAndSortedData` to cover both app_info + app_usage so
                  the tile count and the resulting table size agree. */}
              {data.length > 0 && (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 0.5, mb: 1 }}>
                  {[
                    { label: 'Apps', value: activityCounts.app, filter: 'apps', color: '#42a5f5' },
                    { label: 'Browse', value: activityCounts.browsing, filter: 'browsing', color: '#66bb6a' },
                    { label: 'Calls', value: activityCounts.call, filter: 'call', color: '#26a69a' },
                    { label: 'SMS', value: activityCounts.sms, filter: 'sms', color: '#ab47bc' },
                    { label: 'Status', value: activityCounts.status, filter: 'status', color: '#ff9800' },
                  ].map(({ label, value, filter, color }) => {
                    const isActive = activityTypeFilter === filter;
                    const anyFilter = activityTypeFilter !== 'all';
                    const dimmed = anyFilter && !isActive;
                    return (
                      <Box
                        key={label}
                        onClick={() => setActivityTypeFilter(isActive ? 'all' : filter)}
                        sx={{
                          py: 0.5,
                          borderRadius: 0.5,
                          bgcolor: isActive ? `${color}20` : 'rgba(255,255,255,0.04)',
                          border: '1px solid',
                          borderColor: isActive ? color : 'divider',
                          textAlign: 'center',
                          cursor: 'pointer',
                          opacity: dimmed ? 0.45 : 1,
                          transition: 'border-color 120ms, background-color 120ms, opacity 120ms',
                          '&:hover': { bgcolor: isActive ? `${color}28` : 'rgba(255,255,255,0.06)', opacity: 1 },
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: '0.5rem',
                            textTransform: 'uppercase',
                            letterSpacing: 0.3,
                            display: 'block',
                            lineHeight: 1,
                            color: isActive ? color : 'text.secondary',
                            fontWeight: isActive ? 700 : 400,
                          }}
                        >
                          {isActive ? `${label} ✓` : label}
                        </Typography>
                        <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.75rem', lineHeight: 1.3 }}>
                          {value.toLocaleString()}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* Filters — collapsed by default; shows an "Active" chip when
                  any filter is non-default. Same UX as the Dwell Analysis
                  Filters card so analysts read both panels the same way. */}
              <Accordion
                defaultExpanded={false}
                sx={{
                  mb: 1,
                  '&::before': { display: 'none' },
                  boxShadow: 'none',
                  bgcolor: 'transparent',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '4px !important',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
                  sx={{ minHeight: 28, '&.Mui-expanded': { minHeight: 28 }, py: 0, '& .MuiAccordionSummary-content': { my: 0.25 } }}
                >
                  <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.7rem' }}>
                    Filters
                  </Typography>
                  {filtersActive && (
                    <Chip
                      label="Active"
                      size="small"
                      color="primary"
                      sx={{ ml: 1, height: 14, fontSize: '0.5rem', '& .MuiChip-label': { px: 0.4 } }}
                    />
                  )}
                  <Box sx={{ ml: 'auto', mr: 1, display: 'flex', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                    <Tooltip title={showOnMap ? 'Hide from map' : 'Show on map'}>
                      <IconButton
                        size="small"
                        color={showOnMap ? 'primary' : 'default'}
                        onClick={handleToggleMap}
                        sx={{ p: 0.25 }}
                      >
                        <MapIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Export to file">
                      <span>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={handleExportToPDF}
                          disabled={tableData.length === 0}
                          sx={{ p: 0.25 }}
                        >
                          <DownloadIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, pb: 0.75, px: 1.5 }}>
                  <Stack spacing={1}>
                    <TextField
                      size="small"
                      placeholder="Search activity, entity, content..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      fullWidth
                      InputProps={{
                        endAdornment: <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />,
                        sx: { fontSize: '0.75rem', height: 30 },
                      }}
                    />
                    <FormControl size="small" fullWidth>
                      <InputLabel sx={{ fontSize: '0.7rem' }}>Activity Type</InputLabel>
                      <Select
                        value={activityTypeFilter}
                        label="Activity Type"
                        onChange={(e) => setActivityTypeFilter(e.target.value)}
                        sx={{ fontSize: '0.7rem', '& .MuiSelect-select': { py: 0.5 } }}
                      >
                        <MenuItem value="all">All Types</MenuItem>
                        <MenuItem value="app_info">App Info</MenuItem>
                        <MenuItem value="app_usage">App Usage</MenuItem>
                        <MenuItem value="browsing">Browsing</MenuItem>
                        <MenuItem value="call">Calls</MenuItem>
                        <MenuItem value="sms">SMS</MenuItem>
                        <MenuItem value="status">Device Status</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                </AccordionDetails>
              </Accordion>

              {/* Map Legend */}
              {showOnMap && (
                <Accordion
                  defaultExpanded
                  sx={{
                    mb: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: 'none',
                    '&::before': { display: 'none' },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      minHeight: 36,
                      '&.Mui-expanded': { minHeight: 36 },
                      '& .MuiAccordionSummary-content': { my: 0.75, alignItems: 'center' },
                    }}
                  >
                    <Typography variant="caption" fontWeight={700} sx={{ letterSpacing: 0.3, textTransform: 'uppercase' }}>
                      Map Symbols
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, pb: 1 }}>
                    <Stack spacing={0.75}>
                      {mapSymbolLegend.map((symbol) => {
                        const color = getActivityTypeColor(symbol.type);
                        return (
                          <Box
                            key={symbol.type}
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: '28px 1fr',
                              gap: 1,
                              alignItems: 'center',
                              p: 0.75,
                              pl: 1.25,
                              borderRadius: 1,
                              bgcolor: `${color}1f`,
                              borderLeft: `4px solid ${color}`,
                              border: `1px solid ${color}55`,
                              borderLeftWidth: 4,
                            }}
                          >
                            <Box
                              sx={{
                                width: 26,
                                height: 26,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: color,
                                boxShadow: `0 0 0 2px ${color}44`,
                              }}
                            >
                              <symbol.icon sx={{ fontSize: 16, color: '#fff' }} />
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                variant="caption"
                                sx={{ display: 'block', fontWeight: 700, lineHeight: 1.2, color }}
                              >
                                {symbol.label}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.62rem', lineHeight: 1.2 }}>
                                {symbol.detail}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Data Table.
                  `tableLayout: fixed` + explicit column widths is what keeps
                  the table inside the pane: without it the browser falls back
                  to auto-layout, the Entity cell's `whiteSpace: nowrap` wins,
                  long Activity Details push the row beyond the container and
                  we get a horizontal scrollbar. Activity Details intentionally
                  has NO width set so it absorbs the remaining space and wraps
                  long SMS/URL bodies onto a second line. */}
              <TableContainer
                component={Paper}
                sx={{ maxHeight: 'calc(100vh - 400px)', overflowX: 'hidden' }}
              >
                <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '0.7rem', px: 0.5, width: 28 }}></TableCell>
                      <TableCell sx={{ fontSize: '0.7rem', px: 0.75, width: 64 }}>
                        <TableSortLabel
                          active={sortBy === 'timestamp'}
                          direction={sortBy === 'timestamp' ? sortOrder : 'desc'}
                          onClick={() => handleSort('timestamp')}
                          sx={{ fontSize: '0.7rem', '& .MuiTableSortLabel-icon': { fontSize: '0.7rem' } }}
                        >
                          Time
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.7rem', px: 0.75, width: 110 }}>
                        <TableSortLabel
                          active={sortBy === 'entity_id'}
                          direction={sortBy === 'entity_id' ? sortOrder : 'asc'}
                          onClick={() => handleSort('entity_id')}
                          sx={{ fontSize: '0.7rem', '& .MuiTableSortLabel-icon': { fontSize: '0.7rem' } }}
                        >
                          Entity
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.7rem', px: 0.75, width: 76 }}>
                        <TableSortLabel
                          active={sortBy === 'activity_type'}
                          direction={sortBy === 'activity_type' ? sortOrder : 'asc'}
                          onClick={() => handleSort('activity_type')}
                          sx={{ fontSize: '0.7rem', '& .MuiTableSortLabel-icon': { fontSize: '0.7rem' } }}
                        >
                          Type
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.7rem', px: 0.75 }}>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tableData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            {filterByMapExtent && filteredAndSortedData.length > 0
                              ? `No activity inside the current map view. Pan or zoom out, or turn off "Crop to map view".`
                              : `No data found. Select a ${courseSingular.toLowerCase()} and ${pluralLower} to view activity.`}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      tableData.map((item) => (
                        <React.Fragment key={item.id}>
                          <TableRow
                            hover
                            onClick={() => handleRowClick(item)}
                            sx={{
                              cursor: item.latitude != null && item.longitude != null ? 'pointer' : 'default',
                              backgroundColor: selectedRowId === item.id ? 'action.selected' : 'inherit',
                              '&:hover': {
                                backgroundColor: selectedRowId === item.id ? 'action.selected' : 'action.hover'
                              }
                            }}
                          >
                            <TableCell sx={{ px: 0.25, py: 0.25 }}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleRowExpansion(item.id);
                                }}
                                sx={{ p: 0.25 }}
                              >
                                <ExpandMoreIcon
                                  sx={{
                                    fontSize: 16,
                                    transform: expandedRows.has(item.id) ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.2s',
                                  }}
                                />
                              </IconButton>
                            </TableCell>
                            <TableCell sx={{ px: 0.75, py: 0.5 }}>
                              <Tooltip title={item.timeString}>
                                <Typography
                                  variant="caption"
                                  sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
                                >
                                  {new Date(item.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={{ px: 0.75, py: 0.5, overflow: 'hidden' }}>
                              <Tooltip title={item.entity_id}>
                                <Typography
                                  variant="caption"
                                  fontWeight="bold"
                                  sx={{
                                    fontSize: '0.7rem',
                                    display: 'block',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  {item.entity_id}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={{ px: 0.75, py: 0.5 }}>
                              <Chip
                                label={item.activity_type}
                                size="small"
                                sx={{
                                  bgcolor: getActivityTypeColor(item.activity_type),
                                  color: 'white',
                                  fontSize: '0.6rem',
                                  height: 18,
                                  '& .MuiChip-label': { px: 0.75 },
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ px: 0.75, py: 0.5 }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: '0.7rem',
                                  display: 'block',
                                  whiteSpace: 'normal',
                                  wordBreak: 'break-word',
                                  lineHeight: 1.3,
                                }}
                              >
                                {formatActivityData(item.activity_type, item.activity_data)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                          
                          {/* Expanded Row Details */}
                          <TableRow>
                            <TableCell colSpan={5} sx={{ py: 0, borderBottom: expandedRows.has(item.id) ? undefined : 'none' }}>
                              <Collapse in={expandedRows.has(item.id)} timeout="auto" unmountOnExit>
                                <Box sx={{ 
                                  p: 2, 
                                  bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'grey.50' 
                                }}>
                                  <Typography 
                                    variant="caption" 
                                    fontWeight="bold" 
                                    gutterBottom 
                                    sx={{ 
                                      fontSize: '0.75rem',
                                      color: isDarkMode ? 'grey.300' : 'text.primary',
                                      display: 'block',
                                      mb: 1,
                                    }}
                                  >
                                    Full Details:
                                  </Typography>
                                  <Box
                                    component="pre"
                                    sx={{
                                      fontSize: '0.75rem',
                                      bgcolor: isDarkMode ? 'rgba(0, 0, 0, 0.3)' : 'grey.100',
                                      color: isDarkMode ? 'grey.200' : 'text.primary',
                                      p: 1.5,
                                      borderRadius: 1,
                                      overflow: 'auto',
                                      maxHeight: 200,
                                      border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
                                      fontFamily: 'monospace',
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word',
                                    }}
                                  >
                                    {JSON.stringify(item.activity_data, null, 2)}
                                  </Box>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary">
                  Showing {tableData.length} of {data.length} items
                  {deviceFilteredData.length !== data.length && (
                    <> · {data.length - deviceFilteredData.length} hidden by device toggle</>
                  )}
                  {filterByMapExtent && tableData.length !== filteredAndSortedData.length && (
                    <> · {filteredAndSortedData.length - tableData.length} hidden by map view</>
                  )}
                </Typography>
                {/* Surface the Device List toggle state so the user knows the AI
                    insights / PowerPoint export are scoped to a subset of their
                    devices. We render at most one chip showing the currently
                    enabled classes (e.g. "Devices: phone"). */}
                {deviceFilteredData.length !== data.length && (() => {
                  const enabled = new Set<string>();
                  (Object.values(selectedDevices || {}) as string[][]).forEach((keys) =>
                    (keys || []).forEach((k) => {
                      // keys look like "Student-34-phone" - the final
                      // hyphen-separated token is the device class.
                      const cls = (k.split('-').pop() || '').toLowerCase();
                      if (cls) enabled.add(cls);
                    })
                  );
                  const label = enabled.size > 0
                    ? `Devices: ${Array.from(enabled).join(', ')}`
                    : 'Devices: none';
                  return (
                    <Chip
                      size="small"
                      label={label}
                      color="secondary"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.65rem' }}
                    />
                  );
                })()}
                {activityTypeFilter !== 'all' && (
                  <Chip
                    size="small"
                    label={`Filter: ${
                      activityTypeFilter === 'apps' ? 'Apps' :
                      activityTypeFilter === 'app_info' ? 'App Info' :
                      activityTypeFilter === 'app_usage' ? 'App Usage' :
                      activityTypeFilter === 'browsing' ? 'Browsing' :
                      activityTypeFilter === 'call' ? 'Calls' :
                      activityTypeFilter === 'sms' ? 'SMS' :
                      activityTypeFilter === 'status' ? 'Status' :
                      activityTypeFilter
                    }`}
                    onDelete={() => setActivityTypeFilter('all')}
                    color="primary"
                    sx={{ height: 20, fontSize: '0.65rem' }}
                  />
                )}
                {filterByMapExtent && (
                  <Chip
                    size="small"
                    icon={<MapIcon sx={{ fontSize: 14 }} />}
                    label="Cropped to map"
                    color="primary"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.65rem', '& .MuiChip-icon': { ml: 0.5 } }}
                  />
                )}
                {loading && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={16} />
                    <Typography variant="caption" color="text.secondary">
                      Loading...
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}
        </>
      )}

      {/* AI Insights Tab Content */}
      {activeTab === 1 && (
        <Box>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
            <AIIcon sx={{ color: 'primary.main' }} />
            <Typography variant="subtitle2" fontWeight="bold" sx={{ flex: 1 }}>
              AI Insights - Digital Pattern of Life
            </Typography>
            <Tooltip title="Regenerate Insights">
              <IconButton
                size="small"
                onClick={generateAISummary}
                disabled={isLoadingAI || data.length === 0}
                color="primary"
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            {aiSummary && (
              <Tooltip title="Export Report">
                <IconButton
                  size="small"
                  onClick={handleExportInsights}
                  color="primary"
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>

          {data.length === 0 ? (
            <EmptyState
              dense
              icon={<AIIcon />}
              title="No data loaded yet"
              description="Load data in the Data tab first to generate AI insights."
            />
          ) : (
            <>
              {/* Quick Stats */}
              {analyzeData && (
                <Paper sx={{ p: 1.5, mb: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="caption" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                    Quick Statistics
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip
                      label={`${analyzeData.totalActivities} Activities`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                    <Chip
                      label={`${analyzeData.uniqueEntities} Entities`}
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                    {analyzeData.peakHour !== null && (
                      <Chip
                        label={`Peak Hour: ${analyzeData.peakHour}:00`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                    {Object.entries(analyzeData.activityBreakdown).map(([type, count]) => (
                      <Chip
                        key={type}
                        label={`${type}: ${count}`}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                </Paper>
              )}

              {/* Error Display */}
              {aiError && (
                <Alert severity="error" sx={{ mb: 2, py: 0.5 }}>
                  <Typography variant="caption">{aiError}</Typography>
                </Alert>
              )}

              {/* AI Summary */}
              {isLoadingAI ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                  <Stack spacing={2} alignItems="center">
                    <CircularProgress />
                    <Typography variant="caption" color="text.secondary">
                      Generating AI insights...
                    </Typography>
                  </Stack>
                </Box>
              ) : aiSummary ? (
                <Paper sx={{ p: 2 }}>
                  <Box
                    sx={{
                      '& h3': {
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        mt: 2,
                        mb: 1,
                        color: 'primary.main',
                      },
                      '& h3:first-of-type': {
                        mt: 0,
                      },
                      '& p': {
                        fontSize: '0.875rem',
                        lineHeight: 1.6,
                        mb: 1.5,
                        whiteSpace: 'pre-wrap',
                      },
                    }}
                  >
                    {aiSummary.split('\n').map((line, idx) => {
                      if (line.startsWith('###')) {
                        return (
                          <Typography
                            key={idx}
                            variant="h6"
                            component="h3"
                            sx={{
                              fontSize: '1rem',
                              fontWeight: 'bold',
                              mt: 2,
                              mb: 1,
                              color: 'primary.main',
                            }}
                          >
                            {line.replace(/^###\s*/, '')}
                          </Typography>
                        );
                      } else if (line.trim().startsWith('-') || line.trim().startsWith('*')) {
                        return (
                          <Typography
                            key={idx}
                            component="div"
                            sx={{
                              fontSize: '0.875rem',
                              lineHeight: 1.8,
                              pl: 3,
                              mb: 0.5,
                            }}
                          >
                            • {line.replace(/^[-*]\s*/, '')}
                          </Typography>
                        );
                      } else if (line.trim() === '') {
                        return <Box key={idx} sx={{ mb: 1 }} />;
                      } else {
                        return (
                          <Typography
                            key={idx}
                            component="p"
                            sx={{
                              fontSize: '0.875rem',
                              lineHeight: 1.6,
                              mb: 1.5,
                            }}
                          >
                            {line}
                          </Typography>
                        );
                      }
                    })}
                  </Box>
                </Paper>
              ) : (
                <Alert severity="warning" sx={{ py: 0.5 }}>
                  <Typography variant="caption">
                    Click "Regenerate Insights" to generate AI-powered analysis.
                  </Typography>
                </Alert>
              )}

              {/* Data Summary */}
              {analyzeData && aiSummary && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Analysis Period: {new Date(analyzeData.dateRange.start).toLocaleDateString()} to{' '}
                    {new Date(analyzeData.dateRange.end).toLocaleDateString()}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default DigitalPoL;
